import { Injectable } from '@angular/core';
import * as fromRoot from '@app/store/reducers';
import { AuthSelectors, selectAuth2State, selectAuth2StateState } from '@app/store/reducers';
import { Auth } from '@aws-amplify/auth';
import { Amplify, Credentials, Hub } from '@aws-amplify/core';
import { AWSIoTProvider } from '@aws-amplify/pubsub';
import { CognitoIdentityClient } from '@aws-sdk/client-cognito-identity';
import { FromCognitoIdentityPoolParameters } from '@aws-sdk/credential-provider-cognito-identity';
import { MessageAndTopic } from '@cco/model';
import { select, Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, EMPTY, from, Observable, of, timer } from 'rxjs';
import { delay, filter, map, mergeMap, retry, startWith, switchMap, take, tap } from 'rxjs/operators';
import { v4 } from 'uuid';
import { Auth2Service } from '../auth2/services/auth2.service';
import { PubSub } from './pub-sub';
import * as Sentry from '@sentry/angular-ivy';
import { JwtHelperService } from '@modules/angular-jwt/jwthelper.service';
import { HttpClient } from '@angular/common/http';
import { Auth2States } from '@core/auth2/store/auth2.reducer';

interface GetIdResponse {
  IdentityId: string;
}

@Injectable({
  providedIn: 'root',
})
export class AmplifyService {
  private initStarted = false;
  private initCompleteSource$ = new BehaviorSubject<boolean>(false);
  private initComplete$ = this.initCompleteSource$.pipe(filter(initComplete => !!initComplete));

  // Auth
  private tokenExpiry: number;
  private jwtHelper = new JwtHelperService();
  private region: string;
  private iotDataEndpoint: string;

  get clientId() {
    return this._clientId;
  }

  private _clientId: string;

  private cognitoIdentityPoolParameters: FromCognitoIdentityPoolParameters;

  // IoT
  private iotInitStarted = false;
  private iotInitCompleteSource$ = new BehaviorSubject<boolean>(false);
  private iotInitComplete$ = this.iotInitCompleteSource$.pipe(
    filter(initComplete => !!initComplete),
    // shareReplay(),
    // publishReplay(),
    // refCount(),
  );

  constructor(private store: Store<fromRoot.State>, private authService: Auth2Service, private http: HttpClient) {
  }

  getId$(identityPoolId: string, logins: { [provider: string]: string }): Observable<GetIdResponse> {
    const region = identityPoolId.split(':')[0];
    return this.http.post<GetIdResponse>(`https://cognito-identity.${region}.amazonaws.com/`, {
      IdentityPoolId: identityPoolId,
      Logins: logins,
    }, {
      headers: {
        'Content-Type': 'application/x-amz-json-1.1',
        'x-amz-target': 'AWSCognitoIdentityService.GetId',
      },
    });
  }

  private init$(): Observable<boolean> {
    if (this.initStarted) {
      return this.initComplete$;
    }

    this.initStarted = true;

    Hub.listen('pubsub', (data) => {
      if (data.payload.data.connectionState === 'ConnectionDisrupted') {
        from(Auth.currentCredentials()).pipe(
          filter(credentials => !!credentials?.identityId),
          switchMap(credentials =>
            this.authService.attachIotPolicy(credentials.identityId),
          ),
          delay(10000),
          switchMap(() => from(Auth.currentUserCredentials())),
          switchMap(() => PubSub.restartAWSIoTProvider$()),
        ).subscribe();
      }
    });
    combineLatest([
      this.store.pipe(
        select(AuthSelectors.selectCurrentUser),
        startWith(null),
      ),
      this.store.pipe(
        select(selectAuth2State),
        filter(state => state.state === Auth2States.AUTHENTICATED),
        switchMap(() => this.authService.getIotConnectionInfo$().pipe(startWith(null))),
        startWith(null),
      ),
    ]).pipe(
      filter(
        ([ currentUser, iotConnectionInfo ]) =>
          currentUser != null && iotConnectionInfo != null,
      ),
      take(1),
    ).subscribe(([ currentUser, iotConnectionInfo ]) => {
      const cognitoFederationToken = iotConnectionInfo.idToken;
      this.iotDataEndpoint = iotConnectionInfo.iotDataEndpoint;
      this.region = this.iotDataEndpoint.split('.')[2];
      const providerName = iotConnectionInfo.providerName;
      const userPoolClientId = this.jwtHelper.decodeToken(cognitoFederationToken).aud;
      let identityId: string;

      if (iotConnectionInfo.identityPoolId == null) {
        console.warn('Unable to retrieve Identity Pool ID');
        return;
      }

      this.getId$(iotConnectionInfo.identityPoolId, {
        [providerName]: cognitoFederationToken,
      }).subscribe((getIdResponse) => {
        identityId = getIdResponse.IdentityId;
        localStorage.setItem(
          'aws-amplify-federatedInfo',
          JSON.stringify({
            provider: providerName,
            token: cognitoFederationToken,
            user: currentUser.username,
            expires_at: this.jwtHelper.decodeToken(cognitoFederationToken).exp,
            identity_id: identityId,
          }),
        );

        Amplify.configure({
          Auth: {
            identityPoolId: iotConnectionInfo.identityPoolId,
            region: this.region,
            userPoolWebClientId: userPoolClientId,
            // storage: AmplifyMemoryStorage,
          },
        });

        this.initCompleteSource$.next(true);
      });

      this.updateTokenExpiry(cognitoFederationToken);

      // Check if tokens will expire within 5 minutes every minute and request
      // refreshed tokens if they will
      this.store.pipe(
        select(selectAuth2StateState),
        switchMap((state) => {
          if (state === Auth2States.AUTHENTICATED)
            return timer(60000, 60000);

          return EMPTY;
        }),
        tap(() => {
          const now = Date.now();
          if (this.tokenExpiry - now < 300000) {
            this.authService.getCognitoFederationToken$().subscribe((cognitoFederationToken) => {

              this.updateTokenExpiry(cognitoFederationToken);

              // Update AWS SDK Credentials
              this.cognitoIdentityPoolParameters.logins[providerName] = cognitoFederationToken;

              Credentials.set({
                provider: providerName,
                token: cognitoFederationToken,
                user: currentUser.username,
                expires_at: this.jwtHelper.decodeToken(cognitoFederationToken).exp,
                identity_id: identityId,
              }, 'federation');
            });
          }
        }),
      ).subscribe();

      const logins: { [provider: string]: string } = {};
      logins[providerName] = cognitoFederationToken;
      this.cognitoIdentityPoolParameters = {
        client: new CognitoIdentityClient({
          region: this.region,
        }),
        identityPoolId: iotConnectionInfo.identityPoolId,
        logins,
      };
    });

    return this.initComplete$;
  }

  public iotInit$(): Observable<boolean> {
    if (this.iotInitCompleteSource$.value || this.iotInitStarted) {
      return this.iotInitComplete$;
    }

    this.iotInitStarted = true;

    this.init$().subscribe(() =>
      this.store.pipe(
        select(AuthSelectors.selectCurrentUser),
        filter(currentUser => currentUser != null),
        take(1),
      ).subscribe((currentUser) => {
        this._clientId = `cco-frontend-client-${ currentUser.userPoolId }-${ v4() }`;
        Amplify.addPluggable(
          new AWSIoTProvider({
            clientId: this.clientId,
            aws_pubsub_region: this.region,
            aws_pubsub_endpoint: `wss://${ this.iotDataEndpoint }/mqtt`,
          }),
        );

        this.iotInitCompleteSource$.next(true);
      }),
    );

    return this.iotInitComplete$;
  }

  private updateTokenExpiry(idToken) {
    this.tokenExpiry = this.jwtHelper.getTokenExpirationDate(idToken).getTime();
  }

  public publish$(topics: string | string[], payload: unknown): Observable<void[]> {
    return this.iotInit$().pipe(mergeMap(() => from(PubSub.publish(topics, payload))));
  }

  public publish(topics: string | string[], payload: unknown) {
    this.publish$(topics, payload).pipe(take(1)).subscribe();
  }

  public subscribe$<T>(topicFilters: string | string[]): Observable<MessageAndTopic<T>> {
    return this.iotInit$().pipe(
      mergeMap(() => {
        return PubSub.subscribe(topicFilters, {});
      }),
      retry({
        delay: (error, retryCount) => {
          console.warn('retryCount', retryCount);
          if (retryCount === 1) {
            return from(Auth.currentCredentials()).pipe(
              filter(credentials => !!credentials?.identityId),
              switchMap(credentials =>
                this.authService.attachIotPolicy(credentials.identityId),
              ),
              delay(10000),
              switchMap(() => from(Auth.currentUserCredentials())),
              switchMap(() => PubSub.restartAWSIoTProvider$()),
            );
          }

          if (retryCount > 10) {
            console.error('Exceeded 10 retries', error);
            return EMPTY;
          }

          if (error?.error?.errorCode === 7) {
            console.warn('Trying to recover from error: %s', error.error.message);
            return of(null).pipe(delay(Math.min(60000, Math.pow(2, retryCount) * 500)), switchMap(() => PubSub.restartAWSIoTProvider$()));
          } else if (error?.error?.errorCode === 8) {
            console.warn('Connect failed to IoT endpoint.  Retrying in 10 seconds ...');
            return from(Auth.currentCredentials()).pipe(
              filter(credentials => !!credentials?.identityId),
              switchMap(credentials =>
                this.authService.attachIotPolicy(credentials.identityId),
              ),
              delay(10000),
              switchMap(() => from(Auth.currentUserCredentials())),
              switchMap(() => PubSub.restartAWSIoTProvider$()),
            );
          } else if (error?.error === 'Disconnected, error code: 8') {
            console.warn('Lost connection to IoT endpoint.  Attempting to reconnect ...');
            return of(null).pipe(delay(Math.min(60000, Math.pow(2, retryCount) * 500)), switchMap(() => PubSub.restartAWSIoTProvider$()));
          } else if (error?.error?.message?.startsWith('AMQJS0011E')) {
            console.warn('Trying to recover from error: %s', error.error.message);
            return of(null).pipe(delay(Math.min(60000, Math.pow(2, retryCount) * 500)), switchMap(() => PubSub.restartAWSIoTProvider$()));
          }

          Sentry.captureException(error);

          console.error('Unrecoverable PubSub.subscribe error', error);
          return EMPTY;
        },
      }),
      map((data) => {
        const topicSymbol = Object.getOwnPropertySymbols(data.value).find(
          sym => 'Symbol(topic)' === sym.toString(),
        );
        const topic = data.value[topicSymbol];
        delete data.value[topicSymbol];
        return {
          topic,
          message: data.value,
        };
      }),
    );
  }
}
